import has from "lodash/has";
import isEqualWith from "lodash/isEqualWith";
import map from "lodash/map";
import type { BaseBlock } from "../DataModel.types";
import type { TreeItem } from "./LayersTreeItem";
import {
  type ExtendedTreeItem,
  buildBlockMap,
  processBlock,
} from "./tree-conversion-helpers";
import { findAllChildrenWithNames } from "./tree-utils";

const blocksToTreeItems = (blocks: BaseBlock[]): ExtendedTreeItem[] =>
  blocks.map(block => {
    const treeItem: ExtendedTreeItem = {
      id: block.id,
      type: block.type,
      collapsed: true,
    };

    const childrenGroups = findAllChildrenWithNames(block.props);

    if (childrenGroups.length > 0) {
      treeItem.children = childrenGroups.map(group => ({
        id: `${block.id}-${group.propName}`,
        type: group.propName,
        collapsed: true,
        children: blocksToTreeItems(group.children),
        isPropertyNode: true,
      }));
    } else {
      treeItem.canHaveChildren = false;
    }

    return treeItem;
  });

const blocksToTreeItemsFlat = (blocks: BaseBlock[]): TreeItem[] =>
  blocks.map(block => {
    const treeItem: TreeItem = {
      id: block.id,
      type: block.type,
      collapsed: true,
    };

    const childrenGroups = findAllChildrenWithNames(block.props);
    const allChildren = childrenGroups.flatMap(group => group.children);

    if (allChildren.length > 0) {
      treeItem.children = blocksToTreeItemsFlat(allChildren);
    } else {
      treeItem.canHaveChildren = false;
    }

    return treeItem;
  });

const memoizedConvertBlocksToTree = (() => {
  let cache = new WeakMap<object, TreeItem[]>();
  const MAX_CACHE_SIZE = 1000;
  let cacheSize = 0;

  return (blocks: BaseBlock[], includePropertyNodes = true): TreeItem[] => {
    const cacheKey = { blocks, includePropertyNodes };
    const cached = cache.get(cacheKey);
    if (cached) return cached;

    if (cacheSize >= MAX_CACHE_SIZE) {
      cache = new WeakMap<object, TreeItem[]>();
      cacheSize = 0;
    }

    const result = includePropertyNodes
      ? blocksToTreeItems(blocks)
      : blocksToTreeItemsFlat(blocks);

    cache.set(cacheKey, result);
    cacheSize++;
    return result;
  };
})();

export const convertBlocksToTree = memoizedConvertBlocksToTree;

export const convertTreeToBlocks = (
  treeItems: TreeItem[],
  originalBlocks: BaseBlock[],
): BaseBlock[] => {
  const blockMap = buildBlockMap(originalBlocks);
  const usedBlockIds = new Set<string>();

  const rebuildFromTree = (items: TreeItem[]): BaseBlock[] =>
    items
      .map(item => processBlock(item, blockMap, usedBlockIds, rebuildFromTree))
      .filter((block): block is BaseBlock => block !== null);

  return rebuildFromTree(treeItems);
};

export const areTreesEqual = (a: TreeItem[], b: TreeItem[]): boolean => {
  const normalize = (
    n: TreeItem,
  ): Pick<TreeItem, "id" | "children" | "type"> => ({
    id: n.id,
    type: n.type,
    children: n.children?.map(normalize) ?? [],
  });
  return isEqualWith(a.map(normalize), b.map(normalize), undefined);
};

export const preserveTreeState = (
  items: TreeItem[],
  collapsedState: Record<string, boolean>,
): TreeItem[] => {
  if (!Object.keys(collapsedState).length) return items;
  return map(items, item => {
    const hasState = has(collapsedState, item.id);
    const next: TreeItem = hasState
      ? { ...item, collapsed: collapsedState[item.id] }
      : { ...item };
    if (item.children?.length) {
      const nextChildren = preserveTreeState(item.children, collapsedState);
      if (nextChildren !== item.children) next.children = nextChildren;
    }
    return next;
  });
};

export const findParentIds = (
  items: TreeItem[],
  targetId: string,
): Set<string> => {
  const stack: { items: TreeItem[]; parents: string[] }[] = [
    { items, parents: [] },
  ];
  while (stack.length > 0) {
    const current = stack.pop();
    if (!current) continue;
    const { items: currentItems, parents } = current;
    for (const item of currentItems) {
      if (item.id === targetId) return new Set(parents);
      if (item.children && item.children.length > 0) {
        stack.push({ items: item.children, parents: [...parents, item.id] });
      }
    }
  }
  return new Set();
};
